/**
 * UTM Parameter Tracking Utility
 * 
 * Handles extraction, storage, and persistence of UTM parameters from marketing campaigns.
 * Supports 90-day persistence in localStorage and database storage for attribution tracking.
 */

import { supabase } from '@/lib/supabase'

// localStorage key for storing UTM parameters
const UTM_STORAGE_KEY = 'summer_squad_utm_params'
const SESSION_ID_KEY = 'summer_squad_session_id'
const UTM_EXPIRY_DAYS = 90

/**
 * Extract UTM parameters from a URL
 * @param {string} url - The URL to extract UTM parameters from (defaults to current URL)
 * @returns {Object} Object containing all UTM parameters found
 */
export const extractUTMParams = (url = window.location.href) => {
  try {
    const urlObj = new URL(url)
    const params = new URLSearchParams(urlObj.search)
    
    const utmParams = {
      utm_source: params.get('utm_source') || null,
      utm_medium: params.get('utm_medium') || null,
      utm_campaign: params.get('utm_campaign') || null,
      utm_term: params.get('utm_term') || null,
      utm_content: params.get('utm_content') || null,
      utm_id: params.get('utm_id') || null,
      utm_source_platform: params.get('utm_source_platform') || null,
      utm_creative_format: params.get('utm_creative_format') || null,
      utm_marketing_tactic: params.get('utm_marketing_tactic') || null,
    }
    
    // Only return params that have values
    const filteredParams = {}
    Object.keys(utmParams).forEach(key => {
      if (utmParams[key] !== null) {
        filteredParams[key] = utmParams[key]
      }
    })
    
    return Object.keys(filteredParams).length > 0 ? filteredParams : null
  } catch (error) {
    console.error('Error extracting UTM parameters:', error)
    return null
  }
}

/**
 * Generate a unique session ID for anonymous tracking
 * @returns {string} Session ID
 */
export const generateSessionId = () => {
  // Check if we already have a session ID
  const existingSessionId = localStorage.getItem(SESSION_ID_KEY)
  if (existingSessionId) {
    return existingSessionId
  }
  
  // Generate a new session ID
  const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  localStorage.setItem(SESSION_ID_KEY, sessionId)
  return sessionId
}

/**
 * Get stored UTM parameters from localStorage
 * @returns {Object|null} Stored UTM parameters or null if expired/not found
 */
export const getStoredUTMParams = () => {
  try {
    const stored = localStorage.getItem(UTM_STORAGE_KEY)
    if (!stored) return null
    
    const data = JSON.parse(stored)
    const now = Date.now()
    
    // Check if expired
    if (data.expiresAt && now > data.expiresAt) {
      localStorage.removeItem(UTM_STORAGE_KEY)
      return null
    }
    
    return data.params || null
  } catch (error) {
    console.error('Error reading stored UTM parameters:', error)
    return null
  }
}

/**
 * Store UTM parameters in localStorage with 90-day expiry
 * @param {Object} params - UTM parameters to store
 */
export const storeUTMParams = (params) => {
  if (!params || Object.keys(params).length === 0) return
  
  try {
    const expiresAt = Date.now() + (UTM_EXPIRY_DAYS * 24 * 60 * 60 * 1000)
    const data = {
      params,
      storedAt: Date.now(),
      expiresAt
    }
    
    localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(data))
  } catch (error) {
    console.error('Error storing UTM parameters:', error)
  }
}

/**
 * Clear expired UTM parameters from localStorage
 */
export const clearExpiredUTMParams = () => {
  try {
    const stored = localStorage.getItem(UTM_STORAGE_KEY)
    if (!stored) return
    
    const data = JSON.parse(stored)
    const now = Date.now()
    
    if (data.expiresAt && now > data.expiresAt) {
      localStorage.removeItem(UTM_STORAGE_KEY)
    }
  } catch (error) {
    console.error('Error clearing expired UTM parameters:', error)
  }
}

/**
 * Save UTM data to database
 * @param {Object} params - UTM parameters
 * @param {boolean} isAnonymous - Whether this is an anonymous visitor
 * @param {string} sessionId - Session ID for anonymous tracking
 * @param {string} userId - User ID if authenticated
 * @returns {Promise<Object>} Saved traffic source data
 */
export const saveUTMToDatabase = async (params, isAnonymous = true, sessionId = null, userId = null) => {
  if (!params || Object.keys(params).length === 0) {
    return null
  }
  
  try {
    const finalSessionId = sessionId || generateSessionId()
    const landingPage = window.location.pathname + window.location.search
    const referrer = document.referrer || null
    
    const trafficSourceData = {
      user_id: userId || null,
      session_id: finalSessionId,
      utm_source: params.utm_source || null,
      utm_medium: params.utm_medium || null,
      utm_campaign: params.utm_campaign || null,
      utm_term: params.utm_term || null,
      utm_content: params.utm_content || null,
      utm_id: params.utm_id || null,
      utm_source_platform: params.utm_source_platform || null,
      utm_creative_format: params.utm_creative_format || null,
      utm_marketing_tactic: params.utm_marketing_tactic || null,
      landing_page: landingPage,
      referrer: referrer,
      first_seen_at: new Date().toISOString(),
      last_seen_at: new Date().toISOString(),
    }
    
    // Check if we already have a record for this session with the same UTM params
    // If so, update last_seen_at instead of creating a new record
    if (isAnonymous && finalSessionId) {
      const { data: existing } = await supabase
        .from('user_traffic_sources')
        .select('id, last_seen_at')
        .eq('session_id', finalSessionId)
        .eq('utm_source', params.utm_source || '')
        .eq('utm_medium', params.utm_medium || '')
        .eq('utm_campaign', params.utm_campaign || '')
        .is('user_id', null)
        .order('first_seen_at', { ascending: false })
        .limit(1)
        .single()
      
      if (existing) {
        // Update existing record
        const { data, error } = await supabase
          .from('user_traffic_sources')
          .update({
            last_seen_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .eq('id', existing.id)
        
        if (error) throw error
        return data
      }
    }
    
    // Insert new record
    const { data, error } = await supabase
      .from('user_traffic_sources')
      .insert(trafficSourceData)
      .select()
      .single()
    
    if (error) throw error
    return data
  } catch (error) {
    // Check if table doesn't exist (PGRST205)
    if (error?.code === 'PGRST205') {
      console.warn('user_traffic_sources table does not exist. UTM tracking is disabled. Run the SQL migration: sql/create_user_traffic_sources_table.sql')
      // Don't log as error - this is expected if migration hasn't been run
      return null
    }
    console.error('Error saving UTM data to database:', error)
    // Don't throw - we don't want UTM tracking failures to break the app
    return null
  }
}

/**
 * Link anonymous UTM data to a user account
 * @param {string} userId - User ID to link to
 * @param {string} sessionId - Session ID to find and link
 * @returns {Promise<Object>} Updated traffic source data
 */
export const linkUTMToUser = async (userId, sessionId = null) => {
  if (!userId) return null
  
  try {
    // Get session ID from localStorage if not provided
    const finalSessionId = sessionId || localStorage.getItem(SESSION_ID_KEY)
    if (!finalSessionId) return null
    
    // Find all anonymous records for this session
    const { data: anonymousRecords, error: findError } = await supabase
      .from('user_traffic_sources')
      .select('id')
      .eq('session_id', finalSessionId)
      .is('user_id', null)
    
    if (findError) throw findError
    
    if (!anonymousRecords || anonymousRecords.length === 0) {
      return null
    }
    
    // Update all records to link to user and set converted_at
    const now = new Date().toISOString()
    const recordIds = anonymousRecords.map(r => r.id)
    
    const { data, error } = await supabase
      .from('user_traffic_sources')
      .update({
        user_id: userId,
        converted_at: now,
        updated_at: now
      })
      .in('id', recordIds)
      .select()
    
    if (error) throw error
    
    return data
  } catch (error) {
    // Check if table doesn't exist (PGRST205)
    if (error?.code === 'PGRST205') {
      console.warn('user_traffic_sources table does not exist. UTM tracking is disabled. Run the SQL migration: sql/create_user_traffic_sources_table.sql')
      // Don't log as error - this is expected if migration hasn't been run
      return null
    }
    console.error('Error linking UTM data to user:', error)
    return null
  }
}

/**
 * Get UTM parameters for current session (from URL or storage)
 * @returns {Object|null} UTM parameters
 */
export const getCurrentUTMParams = () => {
  // First check URL for new UTM params
  const urlParams = extractUTMParams()
  if (urlParams) {
    return urlParams
  }
  
  // Fall back to stored params
  return getStoredUTMParams()
}

